import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle, Check } from 'lucide-react';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { supabase } from '../../lib/supabaseClient';

interface PaymentMethod {
  id: string;
  name: string;
}

interface Region {
  id: string;
  code: string;
  name: string;
  currency_code: string;
}

interface RegionMethod {
  id: string;
  region_id: string;
  method_id: string;
  is_active: boolean;
}

interface RegionMethodFormProps {
  method: PaymentMethod;
  regions: Region[];
  onClose: () => void;
}

export const RegionMethodForm: React.FC<RegionMethodFormProps> = ({ 
  method, 
  regions, 
  onClose 
}) => {
  const [selectedRegions, setSelectedRegions] = useState<Record<string, boolean>>({});
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();

  // Fetch existing region methods
  const { data: regionMethods = [], isLoading } = useQuery<RegionMethod[]>(
    ['region-methods', method.id],
    async () => {
      const { data, error } = await supabase
        .from('region_payment_methods')
        .select('*')
        .eq('method_id', method.id);

      if (error) throw error;
      return data;
    }
  );

  // Initialize selected regions based on existing data
  useEffect(() => {
    if (!isLoading && regionMethods.length > 0) {
      const initialSelected: Record<string, boolean> = {};
      
      regionMethods.forEach(rm => {
        initialSelected[rm.region_id] = rm.is_active;
      });
      
      setSelectedRegions(initialSelected);
    }
  }, [isLoading, regionMethods]);

  const handleToggleRegion = (regionId: string) => {
    setSelectedRegions(prev => ({
      ...prev,
      [regionId]: !prev[regionId]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      // Process each region
      for (const region of regions) {
        const isSelected = !!selectedRegions[region.id];
        const existingMethod = regionMethods.find(rm => rm.region_id === region.id);
        
        if (existingMethod) {
          // Update existing record
          if (existingMethod.is_active !== isSelected) {
            await supabase
              .from('region_payment_methods')
              .update({ is_active: isSelected })
              .eq('id', existingMethod.id);
          }
        } else if (isSelected) {
          // Create new record only if selected
          await supabase
            .from('region_payment_methods')
            .insert([{
              region_id: region.id,
              method_id: method.id,
              is_active: true
            }]);
        }
      }
      
      // Invalidate queries
      queryClient.invalidateQueries(['region-methods', method.id]);
      queryClient.invalidateQueries('payment-methods');
      
      onClose();
    } catch (err) {
      console.error('Error saving region methods:', err);
      setError(err instanceof Error ? err.message : 'Failed to save region settings');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 text-center">
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
          onClick={onClose}
        />
        
        <div className="inline-block w-full max-w-md my-8 text-left align-middle transition-all transform">
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl">
            <button
              onClick={onClose}
              className="absolute right-4 top-4 p-2 rounded-full text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Region Availability
              </h2>
              <p className="text-gray-600 dark:text-gray-300 text-sm mb-6">
                Select regions where {method.name} is available
              </p>

              {isLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="max-h-80 overflow-y-auto pr-2">
                    <div className="space-y-2">
                      {regions.map(region => (
                        <div 
                          key={region.id}
                          className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {region.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {region.currency_code}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleToggleRegion(region.id)}
                            className={`w-10 h-6 rounded-full flex items-center transition-colors ${
                              selectedRegions[region.id]
                                ? 'bg-blue-600 justify-end'
                                : 'bg-gray-300 dark:bg-gray-600 justify-start'
                            }`}
                          >
                            <span className={`w-5 h-5 rounded-full transform transition-transform ${
                              selectedRegions[region.id]
                                ? 'translate-x-0 bg-white'
                                : '-translate-x-0 bg-white'
                            }`}>
                              {selectedRegions[region.id] && (
                                <Check className="w-3 h-3 text-blue-600 m-auto mt-1" />
                              )}
                            </span>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 flex items-start space-x-2">
                      <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{error}</span>
                    </div>
                  )}

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Settings'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};